import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import CommonLayout from "../../Common/Layout";
import KMKMenu from "../../Common/KMKMenu";
import PipelineList from "./List/PipelineList";
import JobList from "./List/JobList";

import WorkspaceService from "../../../service/workspace";

import { JOB_TYPES } from "./utils/JobData";
import { getStoredValue, isEqual, setPrevState, setStorageValue } from "../../../util/common";
import { navScheduledJobs } from "../../kmk/AppData";
import DataPipelineService from "../../../service/dataPipelineService";

const useStyles = makeStyles()({
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
  },
  PanelContainer: {
    padding: "30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
  },
});

export default function ScheduledJobs() {
  const { Workspace_id } = useParams();

  const { workspace } = useSelector((state) => state.workspace);
  const { User_id } = useSelector((state) => state.user.currentUser);
  const { Name: workspaceName, IsDataVaultEnabled } = workspace;

  const [scheduleState, setScheduleState] = useState({
    open: false,
    loading: false,
    blockLoading: false,
    loadingForConfirm: false,
  });

  const [dataPipelineList, setDataPipelineList] = useState([]);
  const [selectedPipelines, setSelectedPipelines] = useState([]);

  const [jobList, setJobList] = useState([]);
  const [serverTimeZone, setServerTimeZone] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  const navigate = useRef(useNavigate());
  const { classes } = useStyles();

  const handleJobsMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const $meuOptions = useMemo(
    () =>
      JOB_TYPES.filter((job) => (!IsDataVaultEnabled ? job.kmkEnabled : true)).map((job) => {
        return {
          title: job.Name,
          id: job.type,
          handleClick: () => navigate.current(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/${job.path}/`),
        };
      }),
    [Workspace_id, IsDataVaultEnabled]
  );

  const getDataPipelineList = useCallback(
    async (selectionList) => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);

      if (response.success && response.data?.dataPipelineList?.length) {
        setDataPipelineList(response.data.dataPipelineList);

        // Store in storage
        setStorageValue(`${User_id}_PipelineFilters`, {}, (storedValue) => {
          const allPipelineIds = (response.data.dataPipelineList || [])?.map((pipeline) => pipeline?.DataPipeline_id);

          let selectedPipelineIds = [];
          if (selectionList?.length) selectedPipelineIds = allPipelineIds?.filter((id) => selectionList.includes(id));
          else if (selectionList === undefined) {
            const storedSelection = storedValue?.[Workspace_id]?.selectedPipelines;
            selectedPipelineIds = storedSelection?.length
              ? allPipelineIds?.filter((id) => storedSelection?.includes(id))
              : [];
          }

          // Set selected pipelines
          const selectedAll = isEqual(selectionList?.slice().sort(), allPipelineIds?.slice().sort());
          setSelectedPipelines(selectedAll ? [] : selectedPipelineIds);

          const updatedFilters = {
            ...storedValue,
            [Workspace_id]: { ...storedValue[Workspace_id], selectedPipelines: selectedAll ? [] : selectedPipelineIds },
          };

          return updatedFilters;
        });
      }
    },
    [User_id, Workspace_id]
  );

  const getAllJobList = useCallback(
    async (selectionList) => {
      const storedValue = getStoredValue(`${User_id}_PipelineFilters`, {});

      let pipelineList = [];
      if (selectionList !== undefined) pipelineList = selectionList?.length ? selectionList : [];
      else pipelineList = storedValue?.[Workspace_id]?.selectedPipelines || [];

      const wsService = new WorkspaceService();
      const response = await wsService.getAllJobs(Workspace_id, {
        jobTypeList: storedValue?.[Workspace_id]?.selectedType || JOB_TYPES.map((job) => job.type),
        pipelineList,
        search: storedValue?.[Workspace_id]?.search || "",
        orderBy: storedValue?.[Workspace_id]?.sortedOn?.orderBy || "Name",
        order: storedValue?.[Workspace_id]?.sortedOn?.order || "asc",
      });

      if (response.success) {
        setJobList(response.data.jobs);
        setServerTimeZone(response.data.ServerTimeZone ?? "");
      }
    },
    [User_id, Workspace_id]
  );

  const fetchAllData = useCallback(
    async (selectionList) => {
      setPrevState(setScheduleState, { ...(selectionList !== undefined ? { blockLoading: true } : { loading: true }) });
      await Promise.all([getAllJobList(selectionList), getDataPipelineList(selectionList)]);
      setPrevState(setScheduleState, {
        ...(selectionList !== undefined ? { blockLoading: false } : { loading: false }),
      });
    },
    [getAllJobList, getDataPipelineList]
  );

  useEffect(() => {
    fetchAllData();
    return () => {};
  }, [Workspace_id, fetchAllData]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: "#", displayName: "Data Pipelines", active: true },
  ];

  return (
    <CommonLayout title="Data Pipelines" loading={scheduleState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowX: "hidden",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: 99999 }} open={scheduleState.blockLoading}>
          <CircularProgress color="primary" />
        </Backdrop>
        {scheduleState.loading ? (
          <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="primary" />
          </Backdrop>
        ) : (
          <Box sx={{ mb: 4, mx: 0 }}>
            <Box className={classes.subContainer}>
              <Box className={classes.PanelContainer}>
                {IsDataVaultEnabled !== 0 && (
                  <PipelineList
                    dataPipelineList={dataPipelineList}
                    setDataPipelineList={setDataPipelineList}
                    selectedPipelines={selectedPipelines}
                    setSelectedPipelines={setSelectedPipelines}
                    fetchAllData={fetchAllData}
                    setJobList={setJobList}
                    handleLoading={(flag) => setPrevState(setScheduleState, { blockLoading: flag })}
                  />
                )}
                <JobList
                  scheduleState={scheduleState}
                  setScheduleState={setScheduleState}
                  jobList={jobList}
                  setJobList={setJobList}
                  serverTimeZone={serverTimeZone}
                  handleJobsMenuOpen={handleJobsMenuOpen}
                  selectedPipelines={
                    selectedPipelines || dataPipelineList?.map((pipeline) => pipeline?.DataPipeline_id)
                  }
                  setSelectedPipelines={setSelectedPipelines}
                />
                <KMKMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} meuOptions={$meuOptions} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </CommonLayout>
  );
}
